import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, forkJoin, of, Subscription, switchMap } from 'rxjs';
import { NotificationSettings } from 'shared/interfaces/notification-settings.interface';
import { TabComponentInterface } from 'shared/interfaces/tab-component.interface';
import { EventBusService, FormDifferService, ToastService, UserApiService } from 'shared/services';
import { invalidControlScroll } from 'shared/util/invalid-control-scroll';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';
import { User } from 'users/interfaces/user.interface';
import { SupplierUsersService } from 'users/services/supplier-users.service';

@Component({
    selector: 'app-edit-user-basic',
    templateUrl: './supplier-user-edit-basic.component.html',
    styleUrls: ['./supplier-user-edit-basic.component.scss'],
    standalone: false,
})
export class SupplierUserEditBasicComponent implements OnInit, OnDestroy, TabComponentInterface {
    private subscriptions: { [key: string]: Subscription | undefined } = {};

    public data!: User;
    public user!: User;
    public notifications: NotificationSettings[] = [];
    public userIsAdmin!: boolean;
    public editUserForm: UntypedFormGroup = this.formBuilder.group({
        firstName: new UntypedFormControl(null, [Validators.required, Validators.maxLength(32)]),
        lastName: new UntypedFormControl(null, [Validators.required, Validators.maxLength(32)]),
        isEnabled: new UntypedFormControl(null),
        passwordChangeRequired: new UntypedFormControl(null),
        doNotEmail: new UntypedFormControl(null),
        notificationSettings: this.formBuilder.group({}),
    });

    constructor(
        private formBuilder: UntypedFormBuilder,
        private usersService: SupplierUsersService,
        private eventBusService: EventBusService,
        private userApiService: UserApiService,
        private toastService: ToastService,
        private translate: TranslateService,
        private formDifferService: FormDifferService,
        private elementRef: ElementRef,
        private cdr: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        this.subscriptions.modalMainAction = this.eventBusService.getObservableForEventStream('modalMainButtonAction').subscribe((data) => {
            if (data === 'basic') {
                this.updateUser();
            }
        });

        this.subscriptions.getLoginInformation = this.userApiService
            .getLoginInformation()
            .subscribe((userInformation) => (this.userIsAdmin = userInformation.isShiji));

        this.subscriptions.getUserFullData = this.usersService
            .getSupplierUserById(this.data.id!)
            .pipe(
                filter(Boolean),
                switchMap((userData) => combineLatest([of(userData), this.usersService.getNotificationSettingsByUserId(userData.id)])),
            )
            .subscribe(([userData, notificationSettings]) => {
                this.user = userData;
                this.editUserForm.patchValue(userData);
                this.editUserForm.get('doNotEmail')?.setValue(!userData.emailsEnabled);

                if (userData.passwordChangeRequired) {
                    this.editUserForm.get('passwordChangeRequired')?.disable();
                }

                //Notifications
                this.notifications = notificationSettings;
                this.editUserForm.removeControl('notificationSettings');
                this.editUserForm.addControl('notificationSettings', new UntypedFormGroup({}));
                const notifications = this.editUserForm.get('notificationSettings') as UntypedFormGroup;
                this.notifications.forEach((item: NotificationSettings) => {
                    notifications.addControl(item.id, new UntypedFormControl(item.isActive));
                });
                this.notifications.orderBy('notificationType', false);

                this.formDifferService.registerForm(this.editUserForm);
                this.formDifferService.createDataSnapshot();
            });
    }

    public ngOnDestroy() {
        unsubscribeFromAll(this.subscriptions);
    }

    public canDeactivateTab(): boolean {
        const canDeactivate = !this.formDifferService.hasDataChanged();

        if (!canDeactivate) {
            this.toastService.showToast('generalKeywords.unsavedTabChanges', 'error', {}, 8000);
        }

        return canDeactivate;
    }

    public updateUser() {
        if (this.editUserForm.untouched || Object.keys(this.formDifferService.getDiffWithSnapshot()).length === 0) {
            this.closeModal();
            return;
        }

        this.editUserForm.markAllAsTouched();
        this.editUserForm.updateValueAndValidity();

        if (this.editUserForm.valid) {
            const body = {
                id: this.user.id,
                userType: this.user.userType,
                ...this.formDifferService.getDiffWithSnapshot(),
            } as User;

            const modifiedControls = Object.keys(this.formDifferService.getDiffWithSnapshot());
            const calls = [];

            if (modifiedControls.includes('notificationSettings')) {
                const notificationsBody: NotificationSettings[] = [];
                Object.keys(this.editUserForm.value.notificationSettings).forEach((key) => {
                    notificationsBody.push({
                        id: key,
                        isActive: this.editUserForm.value.notificationSettings[key],
                        userId: this.user.id,
                    } as NotificationSettings);
                });

                calls.push(this.usersService.updateNotificationSettings(notificationsBody));
                modifiedControls.splice(modifiedControls.indexOf('notificationSettings'), 1);
            }

            if (modifiedControls.length) {
                if (modifiedControls.findIndex((key) => key == 'doNotEmail') !== -1) {
                    body['emailsEnabled'] = !this.editUserForm.value.doNotEmail;
                }

                calls.push(this.usersService.updateSupplierUser(body));
            }

            this.subscriptions.forkJoin = forkJoin(calls).subscribe(() => {
                this.toastService.showToast('users.supplierUsers.edit.basic.updateSuccess', 'success', {
                    firstName: this.editUserForm.value.firstName,
                    lastName: this.editUserForm.value.lastName,
                });
                this.closeModal(true);
            });
        } else {
            this.cdr.detectChanges();
            invalidControlScroll(this.elementRef);
        }
    }

    private closeModal(refreshGrid: boolean = false) {
        this.eventBusService.dispatch('closeUserModal', refreshGrid);
    }
}
